import React from 'react'
import { useNavigate } from 'react-router-dom'
import { MapPin, Store, Package, MessageSquare, ArrowRight, DollarSign } from 'lucide-react'
import DataResultsContainer from '../DataResultsContainer/DataResultsContainer.tsx'
import styles from '../SharedResults.module.scss'

interface PortfolioItem {
  id: string | number
  name: string
  description?: string
  type: 'producto' | 'servicio'
  price?: number | string
  priceUnit?: string
  currencyType?: string
  zone?: string
  user?: {
    id?: string | number
    name?: string
    last_name?: string
    razon_social?: string
    type: string
  }
  portfolio_images?: Array<{ url?: string }>
  source: 'portfolio'
  displayName: string
  displayDescription: string
  userDisplayName: string
}

interface PosteoItem {
  id: string | number
  content: string
  images?: Array<{ url?: string }>
  video?: Array<{ url?: string }>
  user?: {
    id?: string | number
    name?: string
    last_name?: string
    razon_social?: string
    type: string
  }
  source: 'posteo'
  displayName: string
  displayDescription: string
  userDisplayName: string
}

type ServiceOrProduct = PortfolioItem | PosteoItem

interface ServicesAndProductsListProps {
  items: ServiceOrProduct[]
  total: number
  noResultsMessage?: string
  openInNewWindow?: boolean
}

const formatPrice = (price?: number | string, priceUnit?: string, currencyType?: string) => {
  if (!price || price === '0' || Number.parseFloat(String(price)) === 0) return null
  const numPrice = typeof price === 'string' ? parseFloat(price) : price
  const currency = currencyType || '$'
  const unit = priceUnit || ''
  return `${currency} ${numPrice.toLocaleString('es-AR')}${unit ? ` / ${unit}` : ''}`
}

const ServicesAndProductsList: React.FC<ServicesAndProductsListProps> = ({
  items,
  total,
  noResultsMessage,
  openInNewWindow = false
}) => {
  const navigate = useNavigate()

  return (
    <DataResultsContainer
      icon={<Store size={18} />}
      title={`${items.length} ${items.length === 1 ? 'servicio o producto' : 'servicios y productos'}`}
      subtitle={total > 3 ? `de ${total} disponibles` : undefined}
      color="blue"
      total={total}
      maxDisplay={3}
      viewAllLink="/marketplace/explorar"
      viewAllText={total > 100 ? `Ver más de 100 servicios y productos` : `Ver los ${total} servicios y productos`}
      emptyMessage={noResultsMessage || 'No se encontraron servicios o productos disponibles.'}
      isEmpty={items.length === 0}
      openInNewWindow={openInNewWindow}
    >
      {items.slice(0, 3).map((item) => {
        const isPortfolio = item.source === 'portfolio'
        const portfolioItem = isPortfolio ? item as PortfolioItem : null
        const priceStr = portfolioItem ? formatPrice(portfolioItem.price, portfolioItem.priceUnit, portfolioItem.currencyType) : null
        const description = item.displayDescription ?? ''

        return (
          <div key={`${item.source}-${item.id}`} className={`${styles.resultCard} ${styles.resultCardBlue}`}>
            <div className={styles.resultCardHeader}>
              <span className={styles.resultCardTitle}>{item.displayName}</span>
              {isPortfolio && (
                <span className={`${styles.resultCardBadge} ${styles.resultCardBadgeBlue}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  {portfolioItem?.type === 'producto' ? (
                    <>
                      <Package size={12} />
                      Producto
                    </>
                  ) : (
                    <>
                      <Store size={12} />
                      Servicio
                    </>
                  )}
                </span>
              )}
              {!isPortfolio && (
                <span className={`${styles.resultCardBadge} ${styles.resultCardBadgeBlue}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <MessageSquare size={12} />
                  Publicación
                </span>
              )}
            </div>

            <p className={styles.resultCardDescription}>
              {description.length > 80
                ? `${description.substring(0, 80)}...`
                : description}
            </p>

            <div className={styles.resultCardMeta}>
              <span>{item.userDisplayName}</span>
              {portfolioItem?.zone && (
                <span><MapPin size={12} /> {portfolioItem.zone}</span>
              )}
              {priceStr && (
                <span><DollarSign size={12} /> {priceStr}</span>
              )}
            </div>

            <button
              type="button"
              className={`${styles.resultCardCta} ${styles.resultCardCtaBlue}`}
              onClick={() => {
                const userId = item.user?.id
                const url = userId ? `/perfil/${userId}?tab=productos` : `/marketplace/explorar`
                if (openInNewWindow) {
                  window.open(`${window.location.origin}${url}`, '_blank')
                } else {
                  navigate(url)
                }
              }}
            >
              Ver perfil <ArrowRight size={14} />
            </button>
          </div>
        )
      })}
    </DataResultsContainer>
  )
}

export default ServicesAndProductsList
